import { map, isNil } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import HighlightText from 'components/HighlightText';
import {
  useShopifyPredictiveSearch,
  predictiveSearchResourcesTypes,
} from 'hooks/useShopifyPredictiveSearch';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';
import { generateSearchUrl, parseQueryString } from 'utils/Url';

const DEFAULT_LIMIT_PREDICTIVE_RESULT = 4;

const SearchPredictiveQueriesResults = ({ currentSearchValue, trackSearchNavigation, origin }) => {
  const { setPredictiveSearchValueState, predictiveSearchResultState } = useShopifyPredictiveSearch(
    {
      predictiveResourceType: predictiveSearchResourcesTypes.QUERIES,
      limitPredictiveResult: DEFAULT_LIMIT_PREDICTIVE_RESULT,
    }
  );

  const generateTrackableSearchUrl = (currentSearchValue, trackingParameters) => {
    const trackingParametersObj = parseQueryString(trackingParameters);

    return generateSearchUrl(currentSearchValue, { origin, ...trackingParametersObj });
  };

  const predictiveQueriesArray = predictiveSearchResultState?.queries;
  const shouldShowPredictiveQueries =
    currentSearchValue !== '' && predictiveQueriesArray?.length > 0;

  useEffect(() => {
    if (!isNil(currentSearchValue) && currentSearchValue !== '') {
      setPredictiveSearchValueState(currentSearchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSearchValue]);

  return (
    <div className="SearchPredictiveQueriesResults">
      <div className="SearchPredictiveQueriesResults__container">
        {shouldShowPredictiveQueries && (
          <div className="SearchPredictiveQueriesResults__hits">
            <p className="SearchPredictiveQueriesResults__recent-searches-label">
              {currentSearchValue == ''
                ? I18n.localize('searchBar.recentSearches')
                : I18n.localize('searchBar.topSuggestions')}
            </p>
            {map(predictiveQueriesArray, predictiveQuery => {
              const searchUrl = generateTrackableSearchUrl(
                predictiveQuery?.text,
                predictiveQuery?.trackingParameters
              );

              return (
                <div className="SearchPredictiveQueriesResults__hit" key={predictiveQuery?.text}>
                  <a
                    href={searchUrl}
                    className="SearchPredictiveQueriesResults__hit-link"
                    onClick={() => {
                      trackSearchNavigation(searchUrl);
                    }}
                  >
                    <HighlightText
                      textToHighlight={currentSearchValue}
                      sentence={predictiveQuery.text}
                    />
                  </a>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <style jsx>
        {`
          .SearchPredictiveQueriesResults {
            display: flex;
            align-items: center;
          }

          .SearchPredictiveQueriesResults__container {
            flex-grow: 1;
          }

          .SearchPredictiveQueriesResults__hits {
            padding: ${Theme.get('spacing') * 2}px ${Theme.get('spacing') * 4}px 0;
          }

          .SearchPredictiveQueriesResults__recent-searches-label {
            font-size: ${Theme.get('font-size-small-px')};
            text-transform: uppercase;
            font-weight: ${Theme.get('font-weight-semi-bold')};
            letter-spacing: ${Theme.get('letter-spacing-large-px')};
            padding: ${Theme.get('spacing') * 1.5}px 0;
            color: ${Theme.get('colors-black-40')};
          }

          .SearchPredictiveQueriesResults__hit-link {
            text-decoration: none;
          }

          .SearchPredictiveQueriesResults__hit {
            font-size: ${Theme.get('font-size-px')};
            line-height: ${Theme.get('line-height')};
            letter-spacing: ${Theme.get('letter-spacing-px')};
            padding-bottom: ${Theme.get('spacing')}px;
          }
        `}
      </style>
      <style jsx global>
        {`
          .aa-Input {
            width: 100%;
            appearance: none;
            outline: none;
            height: ${Theme.get('spacing') * 7}px;
            font-size: ${Theme.get('font-size-px')};
            letter-spacing: ${Theme.get('letter-spacing-px')};
            background: ${Theme.get('colors-grey')};
            box-shadow:
              0 2px 0 0 inset transparent,
              2px 0 0 0 inset transparent,
              -2px 0 0 0 inset transparent;
            -webkit-tap-highlight-color: transparent;
          }

          .Input::placeholder {
            color: ${Theme.get('colors-black-20')};
          }

          .Input:focus,
          .Input:active {
            box-shadow:
              0 2px 0 0 inset ${Theme.get('colors-black')},
              2px 0 0 0 inset ${Theme.get('colors-black')},
              -2px 0 0 0 inset ${Theme.get('colors-black')};
          }

          .ais-Highlight-highlighted {
            font-weight: ${Theme.get('font-weight-semi-bold')};
          }
        `}
      </style>
    </div>
  );
};

SearchPredictiveQueriesResults.propTypes = {
  currentSearchValue: PropTypes.string,
  trackSearchNavigation: PropTypes.func,
  origin: PropTypes.string.isRequired,
};

SearchPredictiveQueriesResults.defaultProps = {
  currentSearchValue: '',
  trackSearchNavigation: () => {},
};

export default SearchPredictiveQueriesResults;
